// The handoff, OAuth-next and redirect-query helpers are registered shared
// functions (functions/index.js) and are reached through `el.call` — no
// sibling imports (FA001). The auth page theme is the one import left: a
// style object with no registered home yet.
import { authPageTheme } from './theme.js'

// Editor runs on SDK auth. The old governance/financials federation
// (configureSdkBridge + loginAll fan-out) was a no-op once federation was
// disabled and is gone with the removed governance Supabase project — the
// SDK login + initializeUserSession fully establish the session.

// Page chrome = LoginWindow (modal-style card). Inside Flex we slot the
// LoginForm (email/password) — RequestAccessButtons (OAuth) is already
// part of LoginWindow itself. Layout: form on left, OAuth on right
// (wraps on mobile via @mobileL inside Flex).
export const signin = {
  extends: 'Page',
  // The tab title. A white-label host shows "<page> / <org>", like every
  // other page (pageTitle); helmet re-runs this factory when the brand lands.
  // While the brand is still loading on such a host the factory answers NO
  // title, so helmet leaves the current one — the edge stamps the org name
  // into the first <title> — instead of flashing "… / Symbols" (relman,
  // vault.pr: 2,487 ms "… / Symbols" before the brand). A platform host keeps
  // its own title. isCustomDomainHost is a shell function; elsewhere it is not
  // registered and the page is a platform surface.
  metadata: (el) => {
    const brandNameNow = el.state?.root?.whitelabelBrand?.name
    if (brandNameNow) return { title: el.call('pageTitle', 'auth.page.signin.titlePlain') }
    const fns = el.context?.functions
    const whiteLabelHost =
      typeof fns?.isCustomDomainHost === 'function' && !!el.call('isCustomDomainHost')
    if (whiteLabelHost) return { title: undefined }
    return { title: el.call('polyglot', 'auth.page.signin.title') }
  },
  flow: 'y',
  align: 'center center',
  padding: 'D C',
  '@mobileL': {
    padding: 'A'
  },
  width: '100%',
  height: '100dvh',
  maxWidth: 'none',
  ...authPageTheme,

  // Initial state so `s.loading` is reactively read from the first
  // paint — CheckButton in LoginForm binds text/disabled/opacity to it.
  state: { loading: false, identifier: '', password: '' },

  onRender: (el) => el.call('stashOAuthNext'),

  // Cross-origin auto-handoff: a signed-in user landing here via the
  // preview's bounce (`?redirect=<previewUrl>`) shouldn't have to retype
  // their credentials. If the SDK already holds a valid session, mint a
  // handoff fragment immediately and bounce back to the preview. Without
  // this the preview's `_redirectToWorkspaceLogin` traps the user in
  // this form even though every credential they'd type is one they
  // already have. Caught live 2026-05-18 on hotcard's preview.
  //
  // AUTH-HANDOFF-NARROW-TARGETS: a target that renders a user project (a
  // `<owner>--<key>` preview host) is handed the session only after the
  // server confirms the project is private AND this user may read it
  // (`confirmHandoffTarget`). The shell's cold-boot guard
  // (redirectIfUnauthed) defers exactly those targets to this hook. A
  // refused project target sends the signed-in user home, never to the
  // target.
  onInit: async (el, s) => {
    if (typeof window === 'undefined') return
    const redirectParam = new URL(window.location.href).searchParams.get('redirect')
    if (!redirectParam) return
    const verdict = el.call('classifyHandoffTarget', redirectParam).verdict
    if (verdict === 'refused') return
    const sdk = el.sdk
    if (!sdk || typeof sdk.getAuthToken !== 'function') return
    const accessToken = sdk.getAuthToken()
    if (!accessToken) return
    let me = null
    try {
      me = await sdk.getMe?.()
    } catch (_) {
      return
    }
    if (!me) return
    if (!(await el.call('confirmHandoffTarget', redirectParam, sdk))) {
      console.warn(
        '[signin] handoff refused: the server did not confirm access to the target project'
      )
      if (verdict === 'confirm') {
        try {
          await el.router('/', el.getRoot())
        } catch (_) {
          /* router not ready — the signed-in user stays on this page */
        }
      }
      return
    }
    // Best-effort refresh-token discovery — SDK doesn't expose a sync
    // accessor but it's persisted under the canonical key.
    const refreshToken = window.localStorage.getItem('symbols_refresh_token') || ''
    const payload = el.call('encodeHandoffPayload', {
      accessToken,
      refreshToken,
      expiresAt: 0,
      expiresIn: 0
    })
    // frank-allow FA401 — full-page handoff to a trusted target (same origin or local dev), fragment only when cross-origin
    window.location.replace(el.call('appendHandoffFragment', redirectParam, payload))
  },

  // WHITELABEL-BRANDED-BOOT-1 — on a host whose org has a branding record,
  // this page renders the standalone branded surface INSTEAD of the generic
  // LoginWindow. `root.whitelabelBrand` is resolved pre-auth and is null on
  // every platform host and on any custom domain without a record, so
  // my.symbols.app / canvas / preview / marketplace are byte-identical to
  // before. `hide` AND an explicit `display` on both slots: `if:` is inert in
  // this v3.14 build and FW-HIDE-UNAPPLIED-1 can clobber a first-run-true
  // `hide` (fixed in unpublished smbls 7031e3d60).
  //
  // ⚠️ Rendered HERE rather than registered as a route: '/signin' and
  // '/login' live in three page maps merged at boot, and a route-level
  // registration was silently out-voted (measured — shellPages held the
  // branded page while the live context resolved the shared one).
  WhitelabelSignin: {
    hide: (el, s) => !(s.root && s.root.whitelabelBrand),
    display: (el, s) => (s.root && s.root.whitelabelBrand ? 'flex' : 'none')
  },

  LoginWindow: {
    hide: (el, s) => !!(s.root && s.root.whitelabelBrand),
    // TWO REAL BRANCHES. This was `: null`, a TOMBSTONE that deletes the
    // INHERITED declaration — and LoginWindow declares `flow: 'column'`
    // (components/LoginWindow.js:115), which is a flex display. So on the
    // ORDINARY non-white-label path — the common case — the erased
    // declaration dropped the window from a flex column to a block, taking
    // its whole internal layout with it.
    // `'flex'` is the value `flow: 'column'` resolves to, and it matches
    // both the sibling `WhitelabelSignin` six lines above and
    // LoginWindow.js:508, which already write two real branches.
    display: (el, s) => (s.root && s.root.whitelabelBrand ? 'none' : 'flex'),
    // THE ADDRESS A FINISHED SIGN-UP HANDS OVER (R2-m10, persona sam typed it
    // again). The sign-up card opens the new account itself when it can
    // (signup.js openSignupSession); when it cannot, it sends the person here
    // with the address in the ROOT state (`_signInEmail`), never in the URL.
    // Read once, here, before the form exists, and cleared. The copy for the
    // field lives on this element (`__handedEmail`), not in state: a `state`
    // block here would REPLACE LoginWindow's own declaration (loading,
    // identifier, password), and the field must not track anything reactive.
    onInit: (el, s) => {
      const handed = s.root && s.root._signInEmail
      if (!handed) return
      el.__handedEmail = handed
      s.quietUpdate({ identifier: handed })
      s.root.update({ _signInEmail: '' })
    },
    Flex: {
      LoginForm: {
        Flex: {
          Identifier: {
            Input: {
              // The handed address, once, at creation. A value bound to the
              // typed `identifier` raced with typing (LoginForm.js); this one
              // reads no state, so typing never writes it back.
              value: (el) => {
                const card = typeof el.lookup === 'function' ? el.lookup('LoginWindow') : null
                return (card && card.__handedEmail) || ''
              }
            }
          },
          Password: {
            Input: {
              // With the address in the field, the password is the one field
              // left: the cursor waits there. `onMounted`, not `onRender`: the
              // form is built before it is attached, and a focus then is lost.
              onMounted: (el) => {
                const card = typeof el.lookup === 'function' ? el.lookup('LoginWindow') : null
                if (!card || !card.__handedEmail) return
                if (el.node && typeof el.node.focus === 'function') el.node.focus()
              }
            }
          }
        }
      }
    },
    onSubmit: async (ev, el, s) => {
      ev.preventDefault()
      // Read from LoginWindow state (mirrored from inputs via their
      // onInput handlers in LoginForm). Reading `input.value` directly
      // here is unreliable — Chrome's password manager clears the
      // type=password field at form-submission time as a privacy
      // measure, so a DOM read returns '' even though the user typed.
      const root = el.node
      const identifier = (
        s.identifier ||
        root.querySelector('input[autocomplete="username"]')?.value ||
        ''
      )
        .trim()
        .toLowerCase()
      const password = s.password || root.querySelector('input[type="password"]')?.value || ''
      if (!identifier || !password) {
        el.call('openNotification', {
          title: el.call('polyglot', 'auth.signin.missingFields.title'),
          message: el.call('polyglot', 'auth.signin.missingFields.message'),
          type: 'error'
        })
        return
      }
      s.update({ loading: true })
      try {
        const pluginSession = el.call('getPluginSessionFromUrl')
        const loginResult = pluginSession
          ? await el.sdk.login(identifier, password, { session: pluginSession })
          : await el.sdk.login(identifier, password)
        // A sign-in on this origin: its refresh token is its own (review D1).
        el.call('markOwnSession')

        await el.call('initializeUserSession', { loginData: loginResult })

        // Cross-origin handoff: when `?redirect=<previewUrl>` is set
        // AND the target passes confirmHandoffTarget (a trusted host, or a
        // project host the server confirms for this user), mint a fragment
        // handoff and replace location instead of routing into the
        // dashboard. This is what makes `https://<x>.preview.symbols.app/...`
        // bounce through `my.symbols.app/signin` and come back signed-in.
        const redirectParam =
          typeof window !== 'undefined'
            ? new URL(window.location.href).searchParams.get('redirect')
            : null
        if (redirectParam && (await el.call('confirmHandoffTarget', redirectParam, el.sdk))) {
          const tokens = loginResult?.tokens || loginResult?.data?.tokens || {}
          const accessToken =
            tokens.accessToken ||
            tokens.access_token ||
            (typeof el.sdk.getAuthToken === 'function' ? el.sdk.getAuthToken() : null)
          const refreshToken = tokens.refreshToken || tokens.refresh_token || ''
          const expiresIn =
            tokens.accessTokenExp?.expiresIn || tokens.expiresIn || tokens.expires_in || 0
          const expiresAt = expiresIn ? Date.now() + expiresIn * 1000 : 0
          if (accessToken) {
            const payload = el.call('encodeHandoffPayload', {
              accessToken,
              refreshToken,
              expiresAt,
              expiresIn
            })
            const target = el.call('appendHandoffFragment', redirectParam, payload)
            // frank-allow FA401 — full-page handoff to a trusted target (same origin or local dev), fragment only when cross-origin
            window.location.replace(target)
            return
          }
        }

        // SIGNUP-INTENT — with no explicit `next`, a paid plan picked on
        // symbols.app/built-with opens its checkout once
        // (functions/signupIntent.js).
        const next =
          (typeof window !== 'undefined'
            ? new URL(window.location.href).searchParams.get('next')
            : null) || el.call('takeSignupCheckoutNext')
        s.update({ loading: false })
        // smbls's router is async; awaiting routes the rejection
        // through the outer try/catch instead of surfacing as an
        // unhandled-in-promise (`Cannot read properties of undefined
        // (reading '/*')` when the auth-state remount transiently
        // leaves `el.getRoot().routes` undefined).
        //
        // withLiveQuery carries the CURRENT address bar's query/hash (e.g.
        // `?claim=credits`) onto a BARE `'/'` bounce — tickets/sonnet.md
        // "auth-page redirects still drop the query" — a bare `'/'`
        // silently dropped it (same class the router ticket fixed one
        // layer up, opus.md workspace 6d146d9a). Only applied when `next`
        // is absent: `next` is already a fully-specified destination
        // (frequently carrying its own query, e.g. `/accept-invite?token=…`
        // from accept-invite.js's `/login?next=…` bounce) — appending the
        // CURRENT page's raw query string on top of it would re-embed a
        // redundant `next=` param and, worse, produce a malformed
        // double-`?` URL whenever `next`'s own target already has a query.
        await el.router(next || el.call('withLiveQuery', '/'), el.getRoot())
      } catch (error) {
        // Classify: an incorrect-credentials response (401 / "password
        // is incorrect" / "invalid credentials") is a normal user-typo
        // path, not a system fault — surface via the in-UI notification
        // below, but log at `debug` instead of `error` so it doesn't
        // pollute /logs/bugs with one warn per failed-typo. Real
        // failures (5xx, network, malformed response) keep the error
        // log so the analyze pipeline still picks them up.
        const msg = error?.message || String(error)
        const isCredentialFailure =
          error?.status === 401 ||
          /password is incorrect/i.test(msg) ||
          /invalid credentials/i.test(msg) ||
          /user not found/i.test(msg)
        if (isCredentialFailure) {
          console.debug('[signin] credential check failed:', msg)
        } else {
          console.error('Failed to sign in:', error)
        }
        s.update({ loading: false })
        el.call('openNotification', {
          title: el.call('polyglot', 'auth.signin.failed'),
          message: error?.message || el.call('polyglot', 'auth.signin.failedMessage'),
          type: 'error'
        })
      }
    }
  }
}
